import type { Metadata } from "grpc-web";
import { getErrorMessage } from "src/util/util";
import { PAYMENT_GRPC_DEADLINE_ENABLED } from "src/util/feature-flags";

/**
 * Deadline das chamadas que finalizam pagamento.
 *
 * Hoje DESLIGADO (ver PAYMENT_GRPC_DEADLINE_ENABLED): o controle de timeout é
 * 100% do backend / middleware. O front não pode ser quem desiste primeiro —
 * o gateway (Rede) passa de 2 min em homologação, e cortar a chamada no meio
 * deixa a cobrança em estado indefinido e pode gerar cobrança duplicada quando
 * o usuário tenta de novo.
 *
 * Mecânica: o grpc-web só emite o header `grpc-timeout` — e só define o timeout
 * do XHR, em 110% do deadline — quando a metadata `deadline` está presente. Sem
 * ela nenhum limite parte do cliente; a chamada fica aberta o tempo que o
 * proxy/back permitir.
 *
 * Este valor só volta a ter efeito se PAYMENT_GRPC_DEADLINE_ENABLED for ligado.
 */
export const PAYMENT_CALL_DEADLINE_MS = 120_000;

/**
 * Metadata das chamadas de pagamento.
 *
 * Com a flag desligada não acrescenta deadline algum: a requisição sai sem
 * `grpc-timeout` e sem timeout de XHR.
 */
export function withPaymentDeadline(metadata: Metadata = {}): Metadata {
  if (!PAYMENT_GRPC_DEADLINE_ENABLED) {
    return { ...metadata };
  }

  return { ...metadata, deadline: String(Date.now() + PAYMENT_CALL_DEADLINE_MS) };
}

/** gRPC status codes que indicam falha de borda, não do serviço. */
const GRPC_DEADLINE_EXCEEDED = 4;
const GRPC_UNAVAILABLE = 14;

const TIMEOUT_TITLE = "Ainda estamos confirmando seu pagamento";

const TIMEOUT_DESCRIPTION =
  "O processamento está demorando mais que o normal. " +
  "Seu pagamento ainda está sendo processado. Aguarde mais um momento...";

/** Mensagens técnicas do transporte que nunca devem chegar ao usuário. */
const TECHNICAL_MESSAGE_PATTERN =
  /unknown content-type|upstream request timeout|http status|xhr|network error|failed to fetch|deadline exceeded/i;

export interface PaymentErrorFeedback {
  title: string;
  description: string;
  icon: "error" | "warning";
}

/**
 * Reconhece falhas que vieram da borda (504/502/503 do proxy, queda de conexão,
 * deadline estourado) em vez do serviço de pagamento.
 *
 * O `AuthorizationInterceptor` marca esses erros com `isGatewayFailure`, porque
 * é lá que dá para medir o tempo decorrido — pelo código do gRPC, um 504 é
 * indistinguível de um 401 atravessando o proxy.
 */
export function isPaymentTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const { isGatewayFailure, code } = error as { isGatewayFailure?: boolean; code?: number };

  return !!isGatewayFailure || code === GRPC_DEADLINE_EXCEEDED || code === GRPC_UNAVAILABLE;
}

/**
 * Mensagem que veio do backend, ou string vazia se não houver uma aproveitável.
 *
 * Ruído de transporte (proxy devolvendo text/plain, XHR caído, deadline) não é
 * resposta do serviço: não diz nada ao usuário e não deve ser exibido.
 */
function getBackendMessage(error: unknown): string {
  const message = getErrorMessage(error, "").trim();

  return message && !TECHNICAL_MESSAGE_PATTERN.test(message) ? message : "";
}

/**
 * Traduz a falha de uma chamada de pagamento em título/descrição para o usuário.
 *
 * Prioridade: mensagem do backend > copy de timeout > fallback do chamador.
 *
 * A mensagem do back é a única que sabe o motivo real da recusa ("cartão sem
 * limite", "cartão expirado", "antifraude"), então ela vem primeiro mesmo quando
 * o erro também carrega marca de falha de borda — um código de status genérico
 * não pode sobrescrever um motivo que o serviço já explicou. Só sem mensagem
 * aproveitável é que caímos no genérico: timeout de borda ganha copy própria
 * (não é "deu erro", é "ainda não sabemos" — e repetir pode cobrar duas vezes),
 * e o resto usa o fallback do chamador.
 */
export function getPaymentErrorFeedback(
  error: unknown,
  fallback: { title: string; description: string },
): PaymentErrorFeedback {
  const backendMessage = getBackendMessage(error);
  if (backendMessage) {
    return { title: fallback.title, description: backendMessage, icon: "error" };
  }

  if (isPaymentTimeoutError(error)) {
    return { title: TIMEOUT_TITLE, description: TIMEOUT_DESCRIPTION, icon: "warning" };
  }

  return { title: fallback.title, description: fallback.description, icon: "error" };
}
